import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { fetchAccountSignatureFlagCodes } from "../services/SignatureFlagsService";
import { getFlagObjectsFromApiCodes } from "../../../shared/dummyPrescriptionFlags";

/**
 * Helper function to get flag color based on flag type
 */
const getFlagColor = (flagType) => {
  const type = (flagType || '').toLowerCase();
  if (type === 'consult') return 'warning';
  if (type === 'hold') return 'error';
  if (type === 'insurance') return 'warning';
  if (type === 'priorauth') return 'info';
  if (type === 'refrigerated') return 'info';
  if (type === 'invalid request') return 'error';
  return 'default';
};

/**
 * EpicFlagsDialog - Common reusable dialog for displaying Epic flags
 * Supports both data formats:
 * 1. From PrescriptionItemsTable: Direct item properties (Flags, PrescriptionFlags, etc.)
 * 2. From MultiPickupDialog: epic_flag_response object
 */
const EpicFlagsDialog = ({
  open,
  onClose,
  item = null,
  epicFlagResponse = null,
  rxNumber = null,
  t = null,
  accountLevelFlags = null,
  accountDetails = null,
  accountId = null,
  signatureRequirementsFulfilled = false,
  signaturePickupId = null,
  epicFlagsOrderId = null,
  itemsWithFlags = null,
  loadingFlags = false,
}) => {
  const [selectedItemIndex, setSelectedItemIndex] = useState(0);
  const itemsList = Array.isArray(itemsWithFlags) ? itemsWithFlags : [];
  const hasMultipleItems = itemsList.length > 1;
  const effectiveItem =
    hasMultipleItems && itemsList[selectedItemIndex]
      ? itemsList[selectedItemIndex]
      : itemsList.length === 1
        ? itemsList[0]
        : item;
  const effectiveEpicFlagResponse =
    (effectiveItem && effectiveItem.epic_flag_response) || epicFlagResponse;
  const effectiveRxNumber =
    (effectiveItem && (effectiveItem.rx_no || effectiveItem.rxNo || effectiveItem.prescriptionNumber || effectiveItem.stock_code)) ||
    rxNumber;

  // Reset selected index when items list changes
  useEffect(() => {
    if (itemsList.length > 0 && selectedItemIndex >= itemsList.length) {
      setSelectedItemIndex(0);
    }
  }, [itemsList.length, selectedItemIndex]);

  // Show "Completed" in header when AOB/Flags are completed for this prescription's order (ticket: flag page completed button)
  const showCompletedOnFlagPage =
    signatureRequirementsFulfilled === true &&
    epicFlagsOrderId != null &&
    signaturePickupId != null &&
    String(epicFlagsOrderId) === String(signaturePickupId);
  // Resolve accountId for GET /accounts/:id/signature-flags: prefer T&C account (accountDetails.id) so we fetch the box's signature flags (e.g. AOB)
  const accountIdFromItem =
    effectiveItem && (effectiveItem.parent_account_id != null
      ? effectiveItem.parent_account_id
      : (effectiveItem.account && effectiveItem.account.id) != null
        ? effectiveItem.account.id
        : effectiveItem.customer_id);
  const effectiveAccountId =
    accountId != null
      ? accountId
      : (accountDetails && accountDetails.id != null
          ? accountDetails.id
          : accountIdFromItem);

  // Fetch account signature flags from API when dialog opens (single source of truth for consistency)
  const [fetchedAccountFlags, setFetchedAccountFlags] = useState([]);
  const [loadingAccountFlags, setLoadingAccountFlags] = useState(false);
  useEffect(() => {
    if (!open || effectiveAccountId == null) {
      setFetchedAccountFlags([]);
      setLoadingAccountFlags(false);
      return;
    }
    setLoadingAccountFlags(true);
    fetchAccountSignatureFlagCodes(effectiveAccountId)
      .then(function (codes) {
        setFetchedAccountFlags(getFlagObjectsFromApiCodes(Array.isArray(codes) ? codes : []));
      })
      .catch(function () {
        setFetchedAccountFlags([]);
      })
      .finally(function () {
        setLoadingAccountFlags(false);
      });
  }, [open, effectiveAccountId]);

  const normalizeData = () => {
    if (effectiveEpicFlagResponse) {
      return {
        flags: effectiveEpicFlagResponse.flags || [],
        prescriptionFlags: effectiveEpicFlagResponse.prescription_flags || [],
        cannotDispenseReasons: effectiveEpicFlagResponse.cannot_dispense_reasons || [],
        reasonNotFillable: effectiveEpicFlagResponse.reason_not_fillable || null,
        fillStatus: effectiveEpicFlagResponse.fill_status || null,
        isDispensable: effectiveEpicFlagResponse.is_dispensable || null,
        status: effectiveEpicFlagResponse.fill_status || null,
      };
    } else if (effectiveItem) {
      return {
        flags: effectiveItem.Flags || [],
        prescriptionFlags: effectiveItem.PrescriptionFlags || [],
        cannotDispenseReasons: effectiveItem.CannotDispenseReasons || [],
        reasonNotFillable: effectiveItem.ReasonNotFillable || null,
        fillStatus: effectiveItem.status || null,
        isDispensable: effectiveItem.IsDispensable || null,
        status: effectiveItem.status || null,
      };
    }
    return {
      flags: [],
      prescriptionFlags: [],
      cannotDispenseReasons: [],
      reasonNotFillable: null,
      fillStatus: null,
      isDispensable: null,
      status: null,
    };
  };

  const flagData = normalizeData();
  // T&C flags: use only our fetch result (no prop fallback) so display is consistent every time
  const accountFlags = fetchedAccountFlags;
  // Helper: append T&C flags to item flags, T&C first, then item flags not already in T&C (by Id)
  const accountIdSet = accountFlags.length > 0
    ? new Set(accountFlags.map(function (f) { return (f.Id || f.id || "").toString().toLowerCase(); }))
    : new Set();
  const appendItemFlags = function (itemFlagList) {
    if (accountFlags.length === 0) return itemFlagList;
    const list = [...accountFlags];
    (itemFlagList || []).forEach(function (f) {
      const id = (f.Id != null ? f.Id : f.id != null ? f.id : "").toString().toLowerCase();
      if (id && !accountIdSet.has(id)) list.push(f);
    });
    return list;
  };
  // Fill-Level and Prescription-Level: always T&C first then item flags (same order every time)
  const fillLevelFlagsToShow = loadingAccountFlags ? flagData.flags : appendItemFlags(flagData.flags);
  const prescriptionLevelFlagsToShow = loadingAccountFlags ? flagData.prescriptionFlags : appendItemFlags(flagData.prescriptionFlags);

  const displayRxNumber = effectiveRxNumber || null;

  const hasAnyFlags = 
    (loadingAccountFlags && effectiveAccountId) ||
    accountFlags.length > 0 ||
    fillLevelFlagsToShow.length > 0 ||
    prescriptionLevelFlagsToShow.length > 0 ||
    flagData.cannotDispenseReasons.length > 0 ||
    flagData.reasonNotFillable ||
    flagData.fillStatus ||
    flagData.isDispensable !== null;

  const closeButtonText = t 
    ? t("Console.ActionPopup.Close", { defaultValue: "Close" })
    : "Close";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          position: "relative",
          overflow: "visible",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          pb: 2,
          pt: 3,
          px: 3,
          background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
          color: "#ffffff",
          position: "relative",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <InfoIcon sx={{ color: "#ffffff", fontSize: "1.75rem" }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: "1.5rem",
                letterSpacing: "0.02em",
                color: "#ffffff",
              }}
            >
              Epic Flags & Status Information
            </Typography>
          </Box>
          {displayRxNumber && (
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                fontWeight: 500,
                fontSize: "0.95rem",
              }}
            >
              RX: {displayRxNumber}
            </Typography>
          )}
        </Box>
        
        <Box sx={{ position: "absolute", right: 16, top: 16, display: "flex", alignItems: "center", gap: 1 }}>
          {showCompletedOnFlagPage && (
            <Button
              size="small"
              startIcon={<CheckCircleIcon />}
              onClick={onClose}
              sx={{
                color: "#ffffff",
                borderColor: "rgba(255, 255, 255, 0.6)",
                "&:hover": {
                  borderColor: "#ffffff",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
              variant="outlined"
            >
              {t ? t("Console.ActionPopup.Completed", { defaultValue: "Completed" }) : "Completed"}
            </Button>
          )}
          <IconButton
            onClick={onClose}
            sx={{
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          backgroundColor: "#f8fafc",
          overflowX: "hidden",
          overflowY: "auto",
          maxHeight: "70vh",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#cbd5e1",
            borderRadius: "4px",
            "&:hover": {
              background: "#94a3b8",
            },
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          {loadingFlags ? (
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              }}
            >
              <CardContent sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: "#64748b", fontStyle: "italic" }}>
                  {t ? t("Console.ActionPopup.LoadingOrderDetails", { defaultValue: "Loading order details…" }) : "Loading order details…"}
                </Typography>
              </CardContent>
            </Card>
          ) : hasMultipleItems ? (
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="epic-flags-rx-select-label">
                  {t ? t("Console.ActionPopup.SelectPrescription", { defaultValue: "Select prescription (RX)" }) : "Select prescription (RX)"}
                </InputLabel>
                <Select
                  labelId="epic-flags-rx-select-label"
                  value={selectedItemIndex}
                  label={t ? t("Console.ActionPopup.SelectPrescription", { defaultValue: "Select prescription (RX)" }) : "Select prescription (RX)"}
                  onChange={(e) => setSelectedItemIndex(Number(e.target.value))}
                  sx={{ backgroundColor: "#ffffff" }}
                >
                  {itemsList.map((it, idx) => (
                    <MenuItem key={idx} value={idx}>
                      RX: {it.rx_no || it.rxNo || it.prescriptionNumber || it.stock_code || (idx + 1)}
                      {it.drug_name || it.drugName || it.name ? ` – ${it.drug_name || it.drugName || it.name}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : null}
          {!loadingFlags && hasAnyFlags ? (
            <Box>
            {/* Account-Level Flags (T&C Signature Flags) - show loading or result for consistency */}
            {(loadingAccountFlags && effectiveAccountId) ? (
              <Card
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#f8fafc",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="body2" sx={{ color: "#64748b", fontStyle: "italic" }}>
                    Loading account signature flags…
                  </Typography>
                </CardContent>
              </Card>
            ) : null}
            {accountFlags.length > 0 && (
              <Card
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: "1px solid #0d9488",
                  backgroundColor: "#f0fdfa",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, pb: 1.5, borderBottom: "2px solid #f1f5f9" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: '#0f766e',
                      }}
                    >
                      Account-Level Flags (Prescription Signature Flags):
                    </Typography>
                    <Chip
                      label={`${accountFlags.length} flag${accountFlags.length > 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        backgroundColor: "#ccfbf1",
                        color: "#0f766e",
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        border: "1px solid #5eead4",
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {accountFlags.map((flag, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 2,
                          border: '1px solid #e2e8f0',
                          borderRadius: 2,
                          backgroundColor: '#ffffff',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#f0fdfa',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip
                            label={flag.DisplayName || flag.Type || flag.Id || 'Unknown Flag'}
                            color={getFlagColor(flag.Type)}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                          {flag.Id && (
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                              ID: {flag.Id}
                            </Typography>
                          )}
                        </Box>
                        {flag.Description && (
                          <Typography variant="body2" sx={{ mt: 0.5, color: '#475569', fontWeight: 500, mb: 0.5 }}>
                            {flag.Description}
                          </Typography>
                        )}
                        {flag.Comments && (
                          <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: '#64748b', mb: 1 }}>
                            Comments: {flag.Comments}
                          </Typography>
                        )}
                        <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {flag.AddedBy && (
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                              <strong>Added by:</strong> {flag.AddedBy}
                            </Typography>
                          )}
                          {flag.AddedDateTime && (
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                              <strong>Date:</strong> {new Date(flag.AddedDateTime).toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Cannot Dispense Reasons */}
            {flagData.cannotDispenseReasons.length > 0 && (
              <Card
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#ffffff",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 700, 
                      mb: 2, 
                      color: '#dc2626',
                      fontSize: '1rem',
                      pb: 1.5,
                      borderBottom: "2px solid #f1f5f9",
                    }}
                  >
                    Cannot Dispense Reasons:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {flagData.cannotDispenseReasons.map((reason, idx) => (
                      <Chip
                        key={idx}
                        label={reason}
                        color="error"
                        sx={{ 
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
            
            {/* Fill-Level Flags (account flags from API when available) */}
            {fillLevelFlagsToShow.length > 0 && (
              <Card
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#ffffff",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, pb: 1.5, borderBottom: "2px solid #f1f5f9" }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: '#1e293b',
                      }}
                    >
                      Fill-Level Flags:
                    </Typography>
                    <Chip 
                      label={`${fillLevelFlagsToShow.length} flag${fillLevelFlagsToShow.length > 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        backgroundColor: "#ede9fe",
                        color: "#7c3aed",
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        border: "1px solid #c4b5fd",
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {fillLevelFlagsToShow.map((flag, idx) => (
                      <Box 
                        key={idx} 
                        sx={{ 
                          p: 2, 
                          border: '1px solid #e2e8f0', 
                          borderRadius: 2, 
                          backgroundColor: '#f8fafc',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#ffffff',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip
                            label={flag.DisplayName || flag.Type || 'Unknown Flag'}
                            color={getFlagColor(flag.Type)}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                          {flag.Id && (
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                              ID: {flag.Id}
                            </Typography>
                          )}
                        </Box>
                        {flag.Description && (
                          <Typography variant="body2" sx={{ mt: 0.5, color: '#475569', fontWeight: 500, mb: 0.5 }}>
                            {flag.Description}
                          </Typography>
                        )}
                        {flag.Comments && (
                          <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: '#64748b', mb: 1 }}>
                            Comments: {flag.Comments}
                          </Typography>
                        )}
                        <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {flag.AddedBy && (
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                              <strong>Added by:</strong> {flag.AddedBy}
                            </Typography>
                          )}
                          {flag.AddedDateTime && (
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                              <strong>Date:</strong> {new Date(flag.AddedDateTime).toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
            
            {/* Prescription-Level Flags (account flags from API when available) */}
            {prescriptionLevelFlagsToShow.length > 0 && (
              <Card
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#ffffff",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, pb: 1.5, borderBottom: "2px solid #f1f5f9" }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: '#1e293b',
                      }}
                    >
                      Prescription-Level Flags:
                    </Typography>
                    <Chip 
                      label={`${prescriptionLevelFlagsToShow.length} flag${prescriptionLevelFlagsToShow.length > 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        backgroundColor: "#dbeafe",
                        color: "#1e40af",
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        border: "1px solid #93c5fd",
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {prescriptionLevelFlagsToShow.map((flag, idx) => (
                      <Box 
                        key={idx} 
                        sx={{ 
                          p: 2, 
                          border: '1px solid #e2e8f0', 
                          borderRadius: 2, 
                          backgroundColor: '#f8fafc',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#ffffff',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip
                            label={flag.DisplayName || flag.Type || 'Unknown Flag'}
                            color={getFlagColor(flag.Type)}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                          {flag.Id && (
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                              ID: {flag.Id}
                            </Typography>
                          )}
                        </Box>
                        {flag.Description && (
                          <Typography variant="body2" sx={{ mt: 0.5, color: '#475569', fontWeight: 500, mb: 0.5 }}>
                            {flag.Description}
                          </Typography>
                        )}
                        {flag.Comments && (
                          <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: '#64748b', mb: 1 }}>
                            Comments: {flag.Comments}
                          </Typography>
                        )}
                        <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {flag.AddedBy && (
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                              <strong>Added by:</strong> {flag.AddedBy}
                            </Typography>
                          )}
                          {flag.AddedDateTime && (
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                              <strong>Date:</strong> {new Date(flag.AddedDateTime).toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
            
            {/* Status Information Card */}
            {(flagData.fillStatus || flagData.isDispensable !== null || flagData.reasonNotFillable || (flagData.status && !flagData.fillStatus)) && (
              <Card
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#ffffff",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 700, 
                      mb: 2, 
                      color: '#1e293b',
                      fontSize: '1rem',
                      pb: 1.5,
                      borderBottom: "2px solid #f1f5f9",
                    }}
                  >
                    Status Information:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {flagData.fillStatus && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', minWidth: 120 }}>
                          Fill Status:
                        </Typography>
                        <Chip
                          label={flagData.fillStatus}
                          sx={{
                            backgroundColor: flagData.fillStatus === 'Delivered' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(148, 163, 184, 0.15)',
                            color: flagData.fillStatus === 'Delivered' ? '#16a34a' : '#64748b',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            border: flagData.fillStatus === 'Delivered' ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(148, 163, 184, 0.3)',
                          }}
                          size="small"
                        />
                      </Box>
                    )}
                    
                    {flagData.isDispensable !== null && flagData.isDispensable !== undefined && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', minWidth: 120 }}>
                          Is Dispensable:
                        </Typography>
                        <Chip
                          label={
                            flagData.isDispensable === 'true' || flagData.isDispensable === true 
                              ? 'Yes' 
                              : flagData.isDispensable === 'false' || flagData.isDispensable === false
                                ? 'No'
                                : 'N/A'
                          }
                          sx={{
                            backgroundColor: (flagData.isDispensable === 'true' || flagData.isDispensable === true)
                              ? 'rgba(34, 197, 94, 0.15)'
                              : 'rgba(220, 38, 38, 0.15)',
                            color: (flagData.isDispensable === 'true' || flagData.isDispensable === true)
                              ? '#16a34a'
                              : '#dc2626',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            border: (flagData.isDispensable === 'true' || flagData.isDispensable === true)
                              ? '1px solid rgba(34, 197, 94, 0.3)'
                              : '1px solid rgba(220, 38, 38, 0.3)',
                          }}
                          size="small"
                        />
                      </Box>
                    )}
                    
                    {flagData.reasonNotFillable && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', minWidth: 120 }}>
                          Reason Not Fillable:
                        </Typography>
                        <Chip
                          label={`Code: ${flagData.reasonNotFillable}`}
                          sx={{
                            backgroundColor: 'rgba(245, 158, 11, 0.15)',
                            color: '#d97706',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            border: '1px solid rgba(245, 158, 11, 0.3)',
                          }}
                          size="small"
                        />
                      </Box>
                    )}
                    
                    {flagData.status && !flagData.fillStatus && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', minWidth: 120 }}>
                          Status:
                        </Typography>
                        <Chip
                          label={flagData.status}
                          sx={{
                            backgroundColor: 'rgba(148, 163, 184, 0.15)',
                            color: '#64748b',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            border: '1px solid rgba(148, 163, 184, 0.3)',
                          }}
                          size="small"
                        />
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
            </Box>
          ) : !loadingFlags ? (
            <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid #e2e8f0",
              backgroundColor: "#ffffff",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                No flags or blocking reasons available for this prescription.
              </Typography>
            </CardContent>
            </Card>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          backgroundColor: "#ffffff",
          borderTop: "1px solid #e2e8f0",
        }}
      >
        <Button 
          onClick={onClose} 
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.875rem",
            px: 3,
            py: 1,
            borderRadius: 2,
            background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
            boxShadow: "0 4px 14px 0 rgba(124, 58, 237, 0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)",
              boxShadow: "0 6px 20px 0 rgba(124, 58, 237, 0.4)",
            },
          }}
        >
          {closeButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EpicFlagsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.object, // For PrescriptionItemsTable format
  epicFlagResponse: PropTypes.object, // For MultiPickupDialog format (epic_flag_response)
  rxNumber: PropTypes.string, // Optional RX number override
  t: PropTypes.func, // Translation function (optional)
  accountLevelFlags: PropTypes.arrayOf(PropTypes.object), // Prescription Signature Flags from GET /accounts/:id/signature-flags (mapped to dummy display names)
  accountDetails: PropTypes.object,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Used to fetch GET /accounts/:id/signature-flags when dialog opens
  signatureRequirementsFulfilled: PropTypes.bool, // Show "Completed" in header when AOB/Flags are done for this order
  signaturePickupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Pickup id for which signature was fulfilled
  epicFlagsOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Order id for the prescription whose flags we're viewing
  itemsWithFlags: PropTypes.arrayOf(PropTypes.object), // From counter table: multiple RX with flags; dialog shows selector
  loadingFlags: PropTypes.bool, // From counter table: show loading while fetching order details
};

EpicFlagsDialog.defaultProps = {
  item: null,
  epicFlagResponse: null,
  rxNumber: null,
  t: null,
  signatureRequirementsFulfilled: false,
  signaturePickupId: null,
  epicFlagsOrderId: null,
  accountLevelFlags: null,
  accountDetails: null,
  accountId: null,
  itemsWithFlags: null,
  loadingFlags: false,
};

export default EpicFlagsDialog;




// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/dialogs/EpicFlagsDialog.jsx